import { clientsApi } from '@modules/clients/entities/client/api';
import { HubException } from '@shared/exceptions';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

type FetchClientResult = {
  existsInCompany: boolean;
  existsForBroker: boolean;
};

const useGetClientByMobile = (companyId: string) => {
  const mobileRef = useRef<string | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    refetch: fetchClientByMobile,
  } = useQuery({
    queryKey: ['useGetClientByMobile', companyId],
    queryFn: async () => {
      if (!mobileRef.current) {
        throw new Error('Mobile number not provided');
      }
      return clientsApi.getClientByMobile(companyId, mobileRef.current);
    },
    retry: 0,
    enabled: false,
    staleTime: 0,
  });

  const fetch = async (mobile: string): Promise<FetchClientResult> => {
    mobileRef.current = mobile;
    const { data: clientData, status } = await fetchClientByMobile();

    if (clientData && status === 'success') {
      try {
        await clientsApi.getCompanyClientDetails(companyId, clientData.id);
        return {
          existsInCompany: false,
          existsForBroker: true,
        };
      } catch (_error) {
        if ((_error as HubException).statusCode === 404) {
          return {
            existsInCompany: true,
            existsForBroker: false,
          };
        }
        throw new Error('Error while fetching client details');
      }
    }

    return {
      existsInCompany: false,
      existsForBroker: false,
    };
  };

  return {
    data,
    isLoading,
    isFetching,
    fetch,
  };
};

export default useGetClientByMobile;
